import { useEffect, useState } from 'react';
import { languages, Language } from './useI18nContext';
import { getLocalStorage, setLocalStorage } from 'src/functions/localStorageHandler';

interface BrowserLanguageDetectionResult {
  detectedLanguage: Language | null;
  shouldShowPopup: boolean;
  dismissPopup: () => void;
  acceptLanguage: () => void;
}

const LANGUAGE_PREFERENCE_KEY = 'language_preference_set';
const POPUP_DISMISSED_KEY = 'language_popup_dismissed';

/**
 * Hook to detect browser language and determine if we should show language switcher popup
 * @param currentLanguage - The current page language
 * @returns Detection result with popup state and handlers
 */
export const useBrowserLanguageDetection = (
  currentLanguage: Language
): BrowserLanguageDetectionResult => {
  const [shouldShowPopup, setShouldShowPopup] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState<Language | null>(null);

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') {
      return;
    }

    // Check for test query parameter (for development testing)
    const urlParams = new URLSearchParams(window.location.search);
    const testLang = urlParams.get('test_lang') as Language;

    // Check if user has already made a language preference
    const hasPreference = getLocalStorage(LANGUAGE_PREFERENCE_KEY) === 'true';
    const hasBeenDismissed = getLocalStorage(POPUP_DISMISSED_KEY) === 'true';

    if (hasPreference || hasBeenDismissed) {
      return;
    }

    // Get browser language (e.g., 'en-US', 'es-MX', 'de', etc.)
    // If test_lang is provided, use that instead
    const browserLang = testLang || navigator.language || (navigator as any).userLanguage;
    const primaryLang = browserLang.split('-')[0] as Language;

    // Check if the detected language is in our supported languages
    if (languages.includes(primaryLang)) {
      // Only show popup if detected language is different from current page language
      if (primaryLang !== currentLanguage) {
        setDetectedLanguage(primaryLang);
        setShouldShowPopup(true);
      } else {
        // User is already on their browser language, set preference
        setLocalStorage(LANGUAGE_PREFERENCE_KEY, 'true');
      }
    }
  }, [currentLanguage]);

  const dismissPopup = () => {
    setShouldShowPopup(false);
    setLocalStorage(POPUP_DISMISSED_KEY, 'true');
  };

  const acceptLanguage = () => {
    setShouldShowPopup(false);
    setLocalStorage(LANGUAGE_PREFERENCE_KEY, 'true');

    // Navigate to the localized path
    if (detectedLanguage && typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      const newPath = detectedLanguage === 'en'
        ? currentPath
        : `/${detectedLanguage}${currentPath}`;
      window.location.href = newPath;
    }
  };

  return {
    detectedLanguage,
    shouldShowPopup,
    dismissPopup,
    acceptLanguage,
  };
};
